import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * Professional cursor:
 *  - Subtle 1px ring that follows with a soft spring (no flame, no orange dot).
 *  - Expands + softens on interactive elements.
 *  - Shows a small contextual label ("View", "Open", "Send") when hovering elements with [data-cursor-label].
 *  - Hides on touch devices.
 */
export default function CustomCursor() {
  const [enabled, setEnabled] = useState(false);
  const [variant, setVariant] = useState("default"); // default | hover | text
  const [label, setLabel] = useState("");

  const mx = useMotionValue(-100);
  const my = useMotionValue(-100);
  const sx = useSpring(mx, { damping: 28, stiffness: 350, mass: 0.4 });
  const sy = useSpring(my, { damping: 28, stiffness: 350, mass: 0.4 });

  // Dot follows instantly
  const dotX = useSpring(mx, { damping: 40, stiffness: 1200, mass: 0.1 });
  const dotY = useSpring(my, { damping: 40, stiffness: 1200, mass: 0.1 });

  const labelRef = useRef("");

  useEffect(() => {
    const isFine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!isFine) return;
    setEnabled(true);
    document.body.classList.add("custom-cursor-on");

    const move = (e) => {
      mx.set(e.clientX);
      my.set(e.clientY);
    };

    const over = (e) => {
      const target = e.target;
      const interactive = target.closest(
        "a, button, [role='button'], input, textarea, select, [data-cursor='hover']"
      );
      const textArea = target.closest("p, h1, h2, h3, h4, h5, h6, span, li");
      const labelEl = target.closest("[data-cursor-label]");

      if (interactive) {
        setVariant("hover");
        if (labelEl) {
          labelRef.current = labelEl.getAttribute("data-cursor-label") || "";
          setLabel(labelRef.current);
        } else {
          setLabel("");
        }
      } else if (textArea) {
        setVariant("text");
        setLabel("");
      } else {
        setVariant("default");
        setLabel("");
      }
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseover", over);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      document.body.classList.remove("custom-cursor-on");
    };
  }, [mx, my]);

  if (!enabled) return null;

  const ringSize = variant === "hover" ? 56 : variant === "text" ? 4 : 30;
  const ringBorder = variant === "text" ? "0" : "1px";
  const ringBg =
    variant === "text"
      ? "var(--text)"
      : variant === "hover"
        ? "color-mix(in srgb, var(--accent) 10%, transparent)"
        : "transparent";

  return (
    <>
      {/* Ring */}
      <motion.div
        style={{
          x: sx,
          y: sy,
          width: ringSize,
          height: ringSize,
          translateX: "-50%",
          translateY: "-50%",
          borderWidth: ringBorder,
          background: ringBg,
        }}
        animate={{ width: ringSize, height: ringSize }}
        transition={{ type: "spring", damping: 25, stiffness: 280 }}
        className="pointer-events-none fixed top-0 left-0 rounded-full border-[color:var(--text)] z-[9999] mix-blend-difference"
      />

      {/* Center dot — always present except in text mode */}
      {variant !== "text" && (
        <motion.div
          style={{
            x: dotX,
            y: dotY,
            translateX: "-50%",
            translateY: "-50%",
          }}
          className="pointer-events-none fixed top-0 left-0 w-1 h-1 rounded-full bg-[color:var(--text)] z-[10000] mix-blend-difference"
        />
      )}

      {/* Contextual label */}
      {label && (
        <motion.div
          style={{ x: sx, y: sy }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          className="pointer-events-none fixed top-0 left-0 z-[10001] translate-x-6 translate-y-6"
        >
          <span className="px-2.5 py-1 rounded-full bg-[color:var(--accent)] text-white text-[10px] font-mono uppercase tracking-widest whitespace-nowrap">
            {label}
          </span>
        </motion.div>
      )}
    </>
  );
}
